import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './TopFold.css';
import logo from '../../../../assets/Asset 2.svg';
import riyadhVideo from '../../../../assets/Riyadh.webm';
import { useLanguage } from '../../../../context/LanguageContext';
import translations from '../../../../translations';

const TopFold = () => {
  const navigate = useNavigate();
  const { language, direction, toggleLanguage } = useLanguage();
  const t = translations[language];
  const [menuOpen, setMenuOpen] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        document.body.classList.add('scrolled');
        document.body.classList.remove('at-top');
      } else {
        document.body.classList.add('at-top');
        document.body.classList.remove('scrolled');
      }
    };

    // Set initial state
    document.body.classList.add('at-top');

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.classList.remove('scrolled', 'at-top');
    };
  }, []);

  // Ensure video resumes playing when returning to the page (mobile fix)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const video = videoRef.current;
      if (document.visibilityState === 'visible' && video && video.paused) {
        video.play().catch(() => { }); // Ignore play errors
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      // Close mobile menu if open
      if (menuOpen) {
        setMenuOpen(false);
      }
    }
  };

  return (
    <section className={`top-fold ${direction === 'rtl' ? 'rtl' : ''}`}>
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        className="top-fold-video"
      >
        <source src={riyadhVideo} type="video/webm" />
        Your browser does not support the video tag.
      </video>

      <nav className="top-fold__nav">
        <div className="container">
          <div className="top-fold__nav-content">
            <div className="top-fold__nav-pill">
              <div className={`top-fold__logo${language === 'ar' ? ' top-fold__logo--ar' : ' top-fold__logo--en'}`}>
                <img src={logo} alt="Advanced Elements" className="top-fold__logo-img" />
              </div>
              <div className="top-fold__menu">
                <ul className="top-fold__menu-list">
                  <li className="top-fold__menu-item">
                    <a href="#mission-vision" onClick={(e) => scrollToSection(e, 'mission-vision')}>
                      {t.aboutUs}
                    </a>
                  </li>
                  <li className="top-fold__menu-item">
                    <a href="#services" onClick={(e) => scrollToSection(e, 'services')}>
                      {t.services}
                    </a>
                  </li>
                  <li className="top-fold__menu-item">
                    <a href="#partners" onClick={(e) => scrollToSection(e, 'partners')}>
                      {t.partnersTitle}
                    </a>
                  </li>
                  <li className="top-fold__menu-item">
                    <a href="#projects" onClick={(e) => scrollToSection(e, 'projects')}>
                      {t.projects}
                    </a>
                  </li>
                  <li className="top-fold__menu-item">
                    <a href={`/${language}/contact`} onClick={(e) => {
                      e.preventDefault();
                      navigate(`/${language}/contact`);
                    }}>
                      {t.contact}
                    </a>
                  </li>
                </ul>
              </div>

              <div className="top-fold__actions">
                <button
                  className="btn btn--secondary language-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLanguage();
                  }}
                >
                  {language === 'en' ? 'AR' : 'EN'}
                </button>
                <button
                  className="btn btn--primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/${language}/contact`);
                  }}
                >
                  {t.getStarted}
                </button>
              </div>
              {/* Hamburger button for mobile */}
              {!menuOpen && (
                <div className="hamburger-container">
                  <div className="hamburger" onClick={toggleMenu} aria-label="Open menu" tabIndex={0} role="button">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}
            </div>
            <div className={`mobile-menu ${menuOpen ? 'active' : ''}`}>
              {menuOpen && (
                <div className="mobile-menu__close-btn-container">
                  <div className="hamburger active" onClick={toggleMenu} aria-label="Close menu" tabIndex={0} role="button">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}
              <ul className="mobile-menu__list">
                <li className="mobile-menu__item">
                  <a href="#mission-vision" onClick={(e) => {
                    scrollToSection(e, 'mission-vision');
                    toggleMenu();
                  }}>
                    {t.aboutUs}
                  </a>
                </li>
                <li className="mobile-menu__item">
                  <a href="#services" onClick={(e) => {
                    scrollToSection(e, 'services');
                    toggleMenu();
                  }}>
                    {t.services}
                  </a>
                </li>
                <li className="mobile-menu__item">
                  <a href="#partners" onClick={(e) => {
                    scrollToSection(e, 'partners');
                    toggleMenu();
                  }}>
                    {t.partnersTitle}
                  </a>
                </li>
                <li className="mobile-menu__item">
                  <a href="#projects" onClick={(e) => {
                    scrollToSection(e, 'projects');
                    toggleMenu();
                  }}>
                    {t.projects}
                  </a>
                </li>
                <li className="mobile-menu__item">
                  <a href={`/${language}/contact`} onClick={(e) => {
                    e.preventDefault();
                    navigate(`/${language}/contact`);
                    toggleMenu();
                  }}>
                    {t.contact}
                  </a>
                </li>
              </ul>
              <div className="mobile-menu__actions">
                <button
                  className="btn btn--secondary language-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLanguage();
                    toggleMenu();
                  }}
                >
                  {language === 'en' ? 'AR' : 'EN'}
                </button>
                <button
                  className="btn btn--primary mobile-menu__get-started-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/${language}/contact`);
                    toggleMenu();
                  }}
                >
                  {t.getStarted}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container">
        <div className="top-fold__hero">
          <div className="top-fold__content">
            <div className="top-fold__heading-wrapper">
              <h1 className="top-fold__heading">
                {language === 'en' ? (
                  <>Building <span className="top-fold__highlight" style={{ whiteSpace: 'nowrap' }}>The Future</span></>
                ) : (
                  <>نبني <span className="top-fold__highlight">المستقبل</span></>
                )}
              </h1>
            </div>
            <p className="top-fold__description">
              {language === 'ar' ?
                'تقدم شركة العناصر المتقدمة للمقاولات العامة احترافية استثنائية ودقة وتنفيذ فعال لأكثر مشاريع البناء طموحاً في المملكة العربية السعودية، دعماً لرؤية المملكة 0302.' :
                t.companyDescription
              }
            </p>
            <div className="top-fold__cta">
              <button
                className="btn btn--primary btn--large btn--projects"
                onClick={(e) => scrollToSection(e, 'projects')}
              >
                {t.ourProjects}
              </button>
              <button
                className="btn btn--outline btn--large"
                onClick={(e) => scrollToSection(e, 'services')}
              >
                {t.learnMore}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section >
  );
};

export default TopFold; 